import { useEffect, useRef, useState } from "react";

import { Autocomplete, Chip, IconButton, TextField, InputAdornment, Box, Typography, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import customerService from "@plannel/services/data/customer-service";
import debug from "@plannel/utils/debug-util";
import { useTranslation } from "react-i18next";
import { isEmpty, isEqual, isNull } from "lodash";
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import companyService from "@plannel/services/data/company-service";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import { useDispatch } from "react-redux";
import reduxUtil from "@plannel/utils/redux-util";
import "@plannel/scss/material-ui/HighlightAutocomplete.scss";
import AutocompleteGroupHeader from "@plannel/components/filter/AutocompleteGroupHeader";
import colorUtil from "@plannel/pages/utils/color-util";
import Snackbar from "@plannel/components/Snackbar";
import { FS } from "@plannel/utils/font-util";

const PREFIX_CHECK_HRCHY_GROUP = "customerHrchyLevel";
const PREFIX_HRCHY = "#HRCHY#";
const GROUP_KEY = "groupKey";
const CUSTOMER_ATTR_CUSTOM = "CUSTOMER_AUTOCOMPLETE_ATTR_CUSTOM";
const CUSTOMER_HRCHY_DATA = "CUSTOMER_AUTOCOMPLETE_HRCHY_DATA";
const DEBOUNCE_TIME = 400;
const { snackBarMessageColor } = colorUtil;
const CustomerAutocomplete = ({ retrieve, searchValue, setSelectedCustomer, searchFields, sx, selectedObj = true, searchIcon = true, useAccessFilter = true, onMultiplePaste, extraCodesRef }) => {
  /* Redux Settings */
  const reduxDispatch = useDispatch();
  const [customerAttrCustom, setCustomerAttrCustom] = useState([]);
  const [customerHrchyGroupData, setCustomerHrchyGroupData] = useState([]);

  const { t } = useTranslation();
  const [searchCustomers, setSearchCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [extraCodes, setExtraCodes] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const isPasteUpdate = useRef(false);

  /* 선택하거나 Enter로 선택하는경우 dropdown list가 한번더 조회되어 방지하기 위한 변수 */
  const selectOrEnter = useRef(false);
  const debounceRef = useRef(null);
  const [inputValue, setInputValue] = useState(searchValue?.current ?? "");

  const getCustomerAttributeCustomConfig = () => {
    const CustomerAttributeReduxViewState = reduxUtil.getViewState(CUSTOMER_ATTR_CUSTOM);

    if (CustomerAttributeReduxViewState?.customerAttrCustom) {
      setCustomerAttrCustom(CustomerAttributeReduxViewState.customerAttrCustom);
    } else {
      let itemAttributeFormData = {};

      companyService.getCustomConfig()
        .then(response => {
          const result = response?.data?.customer;
          if (result) {
            setCustomerAttrCustom(result);
            itemAttributeFormData = result;
          } else {
            setCustomerAttrCustom([]);
          }

          const customerAttributeReduxState = reduxUtil.createObjectState(CUSTOMER_ATTR_CUSTOM, { customerAttrCustom: itemAttributeFormData });
          reduxDispatch(reduxUtil.updateViewState(customerAttributeReduxState));
        })
        .catch(e => {
          debug(e);
        })
    }
  }

  const getCustomerHrchyGroupData = () => {
    const customerHrchyReduxViewState = reduxUtil.getViewState(CUSTOMER_HRCHY_DATA);

    if (customerHrchyReduxViewState?.customerHrchyData) {
      setCustomerHrchyGroupData(customerHrchyReduxViewState.customerHrchyData);
    } else {
      let customerHrchyFormData = [];

      customerHrchyService.getAll()
      .then(response => {
        const result = response?.data?.results;
        if(result) {
          setCustomerHrchyGroupData(result);
          customerHrchyFormData = result;
        } else {
          setCustomerHrchyGroupData([]);
        }

        const customerHrchyReduxState = reduxUtil.createObjectState(CUSTOMER_HRCHY_DATA, { customerHrchyData: customerHrchyFormData });
        reduxDispatch(reduxUtil.updateViewState(customerHrchyReduxState));
      })
      .catch(e => {
        debug(e);
      })
    }
  }

  // Group Key Settings을 위한 함수
  const setGroupKeyData = (dataList) => {
    for (const dataObj of dataList) {
      // data Obj는 단일 (Key, Value) 형식으로 구성되어 있음.
      let groupValue = Object.keys(dataObj).length > 1 ? "" : Object.keys(dataObj)[0];

      if (!isEmpty(groupValue) && groupValue.toUpperCase().startsWith(PREFIX_CHECK_HRCHY_GROUP.toUpperCase())) {
        // Group Key 설정을 위한 Customer Hrchy Config에 mapping된 level name으로 설정
        for (const customerHrchy of customerHrchyGroupData) {
          if (customerHrchy?.name === dataObj?.[groupValue]) {
            groupValue = PREFIX_HRCHY + customerHrchy?.hrchyLvName;
            break;
          }
        }
      }

      dataObj[GROUP_KEY] = groupValue;
    }
  }

  const searchCustomer = (e, value) => {
    if(!e) {
      return;
    }

    if (isPasteUpdate.current) {
      isPasteUpdate.current = false;
      return;
    }

    if (extraCodesRef?.current?.length > 0) {
      return;
    }

    if (extraCodesRef && extraCodes.length > 0) {
      setExtraCodes([]);
      extraCodesRef.current = [];
    }

    searchValue.current = value;
    setInputValue(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if(searchValue.current !== ""){
        const params = { value, searchFields, useAccessFilter };
        customerService.search(params)
          .then((e) => {
            const data = e.data;
  
            if(data && data.length > 0) {
              setGroupKeyData(data);
            }
  
            setOpen(false);
            setSearchCustomers(data);
          })
          .catch(e => {
            debug(e);
          })
      } else {
        setSearchCustomers([]);
      }
    }, DEBOUNCE_TIME);

  }

  const onClickHandler = () => {
    if (searchCustomers?.length > 0) {
      setOpen(!open);
    }
  }

  /*
  * 일반적으로 Autocomplete는 list를 한번만 담아오게끔 사용하지만 
  * Elasticsearch를 사용하여 list의 품목 제한을 두기 위해 list를 계속해서 담아오는 방식을 사용
  * 이 과정에서 렌더링이 한번더 발생하기때문에 timeout 설정
  */
  const timeOutClosePopup = () => {
    setTimeout(() => {
      setOpen(false);
    }, 100);
  }

  /*
  * 일반적으로 Autocomplete는 list를 한번만 담아오게끔 사용하지만 
  * Elasticsearch를 사용하여 list의 품목 제한을 두기 위해 list를 계속해서 담아오는 방식을 사용
  * 이 과정에서 렌더링이 한번더 발생하기때문에 timeout 설정
  * List에서 값을 선택하는 경우 한번더 search가 되기때문에 timeout 설정
  */
  const selectOrEnterHandler = () => {
    selectOrEnter.current = true;

    setTimeout(() => {
      selectOrEnter.current = false;
    }, 1000);
  }

  const onKeyUpHandler = event => {
    if (event.key === "Enter") {
      if(!setSelectedCustomer){
        selectOrEnterHandler();
        retrieve();
      }
      timeOutClosePopup();
    }
  }

  const onChangeHandler = (_, e, reason) => {
    if (reason === "clear") {
      searchValue.current = "";
      setInputValue("");
      if (extraCodesRef) {
        setExtraCodes([]);
        extraCodesRef.current = [];
      }
      return;
    }
    if (setSelectedCustomer) {
      if (selectedObj) {
        if (!isNull(e)) {
          setSelectedCustomer(e);
        }
      } else {
        for (const attr in e) {
          if (Object.prototype.hasOwnProperty.call(e, attr)) {
            if (isEqual(e[attr], searchValue.current) && !isEmpty(searchValue.current)) {
              setSelectedCustomer(e[attr]);
              return;
            }
            setSelectedCustomer("");
          }
        }
      }
    } else {
      selectOrEnterHandler();
      timeOutClosePopup();
    }
  }

  /*
   * 그룹 안의 항목들. 값만 보여준다 — 예전에는 줄마다 `거래처 명 =` 을 붙였는데,
   * 바로 위 머리글이 이미 그 이름이라 한 그룹의 모든 줄이 같은 접두어를 되풀이했다.
   */
  const groupChildrenReform = (originGroupKey, children) => {
    return children.map((child) => {
      const key = child.props.id;
      const value = child.key;
      const matches = match(value, searchValue.current, { insideWords: true });
      const parts = parse(value, matches);

      //TODO: 추후 AWS에서 세부검색을 위한 코드
      // const customOptionOnClickHandler = (e, originGroupKey, value) => {
      //   console.log("originGroupKey : ", originGroupKey);
      //   console.log("value : ", value);
      //   child.props.onClick(e);
      // }

      return (
        <Box {...child.props} key={key} sx={{ display: "inline-flex", pl: "14px !important", width: "fit-content" }}>
          <Box>
            {/* key 는 인덱스다 — 검색어가 값 안에 두 번 나오면 조각 텍스트가 겹쳐 중복 key 경고가 났다 */}
            {parts.map((part, partIdx) => (
              <Typography component={"span"} key={partIdx} sx={{ fontSize: FS.TITLE, fontWeight: part.highlight ? 700 : 400, color: part.highlight ? "primary.main" : "text.primary" }}>
                {part.text}
              </Typography>
            ))}
          </Box>
        </Box>
      )
    })
  }

  useEffect(() => {
    if(searchCustomers.length > 0 && searchValue.current === "") {
      setSearchCustomers([]);
    } else if(searchCustomers.length > 0 && !selectOrEnter.current) {
      setOpen(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCustomers])

  useEffect(() => {
    getCustomerAttributeCustomConfig();
    getCustomerHrchyGroupData();
    if (extraCodesRef?.current?.length > 0) {
      setExtraCodes([...extraCodesRef.current]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInputValue(searchValue?.current ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue?.current]);

  const onPasteHandler = (e) => {
    if (!onMultiplePaste && !extraCodesRef) return;

    const pastedText = e.clipboardData.getData("text");
    const hasNewline = /\r?\n/.test(pastedText);
    const hasTab = pastedText.includes("\t");

    if (!hasNewline && !hasTab) return;

    const rows = pastedText.split(/\r?\n/).map(v => v.trim()).filter(Boolean);
    const rawCodes = rows.flatMap(row => row.split("\t").map(v => v.trim())).filter(Boolean);
    let codes = [...new Set(rawCodes)];

    if (codes.length <= 1) return;

    const warnings = [];
    if (codes.length < rawCodes.length) {
      warnings.push(t("MSG_DuplicateData"));
    }
    if (codes.length > 100) {
      codes = codes.slice(0, 100);
      warnings.push(t("MSG_MaxPasteLimit", { max: 100 }));
    }
    if (warnings.length > 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: warnings.join(" ") });
    }

    if (codes.length > 0) {
      e.preventDefault();
      if (onMultiplePaste) {
        onMultiplePaste(codes);
      } else if (extraCodesRef) {
        isPasteUpdate.current = true;
        searchValue.current = codes[0];
        const rest = codes.slice(1);
        setExtraCodes(rest);
        extraCodesRef.current = rest;
      }
    }
  };

  return (
    <>
    <Autocomplete
      value={extraCodes.length > 0 ? "" : (searchValue?.current ? searchValue.current : "")}
      inputValue={extraCodes.length > 0 ? "" : inputValue}
      options={searchCustomers}
      getOptionLabel={option => {
        for (const attr in option) {
          if(!isEqual(attr, GROUP_KEY) && !isEmpty(searchValue.current) && option[attr] && option[attr].toUpperCase().includes(searchValue.current.toUpperCase())) {
            return option[attr];
          }
        }

        if (typeof option !== "string") {
          for (const attr in option) {
            if (!isEqual(attr, GROUP_KEY) && !isEmpty(searchValue.current) && option[attr]) {
              return option[attr];
            }
          }
        }

        return searchValue.current;
      }}
      onChange={onChangeHandler}
      onClose={() => setOpen(false)}
      onOpen={onClickHandler}
      onClick={onClickHandler}
      open={open}
      onKeyUp={onKeyUpHandler}
      onInputChange={(e, newValue) => searchCustomer(e, newValue)}
      freeSolo
      size="small"
      sx={{ minWidth: 230, width: 240, mr: 1, ...sx }}
      renderInput={(params) => (
        <TextField {...params} InputLabelProps={{ shrink: open || !isEmpty(searchValue.current) || extraCodes.length > 0 }} label={t("customer")}
          inputProps={{ ...params.inputProps, onPaste: onPasteHandler,
            onBeforeInput: extraCodes.length > 0 ? (e) => { if (e.inputType !== 'insertFromPaste') e.preventDefault(); } : undefined,
            style: { ...params.inputProps?.style, ...(extraCodes.length > 0 ? { width: 0, minWidth: 0, paddingLeft: 0, paddingRight: 0 } : {})}
          }}
          InputProps={{
            ...params.InputProps,
            sx: { pr: '6px !important' },
            startAdornment: extraCodes.length > 0 ? (
              <Tooltip title={[...extraCodes].join(", ")} placement="bottom-start">
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, gap: 0.5, minWidth: 0, overflow: 'hidden', flexShrink: 1 }}>
                  <Box component="span" sx={{ fontSize: FS.TITLE, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {searchValue.current}
                  </Box>
                  <Chip
                    size="small"
                    label={`+${extraCodes.length}`}
                    sx={{ flexShrink: 0, mr: 0.5, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: FS.BODY, lineHeight: 1 } }}
                  />
                </Box>
              </Tooltip>
            ) : null,
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InputAdornment position="end">
                  {params.InputProps.endAdornment?.props?.children}
                  {searchIcon && <IconButton aria-label="search" size="small"
                                             onClick={() => {
                                               if (setSelectedCustomer) {
                                                 // searchCustomers에서 현재 입력값과 매칭되는 첫 번째 항목 찾기
                                                 const matchedCustomer = searchCustomers.find(customer => {
                                                   for (const attr in customer) {
                                                     if (!isEqual(attr, GROUP_KEY) && customer[attr] === searchValue.current) {
                                                       return true;
                                                     }
                                                   }
                                                   return false;
                                                 });
                                                 setSelectedCustomer(matchedCustomer || searchValue.current || "");
                                               } else {
                                                 retrieve();
                                               }
                                             }} >
                    <SearchIcon /></IconButton>}
                </InputAdornment>
              </Box>
            )
          }}
        />
      )}
      groupBy={(option) => option.groupKey}
      renderGroup={(params) => {
        const originGroupKey = params.group;
        let groupKey = params.group;
        
        const customerAttrCustomValue = customerAttrCustom?.[params.group];
        if(groupKey.startsWith(PREFIX_HRCHY)) {
          groupKey = groupKey.replace(PREFIX_HRCHY, "");
        } else if(!isEmpty(customerAttrCustomValue)) {
          groupKey = customerAttrCustomValue;
        } else {
          groupKey = t(groupKey);
        }

        return (
          <AutocompleteGroupHeader key={params.key} label={groupKey}>
            {groupChildrenReform(originGroupKey, params.children)}
          </AutocompleteGroupHeader>
        )
      }}
    />
    <Snackbar
      open={snackInfo.open}
      onClose={() => setSnackInfo({ ...snackInfo, open: false })}
      severity={snackInfo.severity}
      content={snackInfo.content}
    />
    </>
  )
}

export default CustomerAutocomplete;